import { useState } from "react";
import { Send } from "lucide-react";

type Field = "name" | "phone" | "message";

function validate(values: Record<Field, string>) {
  const errors: Partial<Record<Field, string>> = {};
  const name = values.name.trim();
  const phone = values.phone.trim();
  const message = values.message.trim();

  if (!name || name.length > 100) errors.name = "Please enter your name";
  if (!phone || phone.length < 7 || phone.length > 20 || !/^[\d\s()+-]+$/.test(phone)) {
    errors.phone = "Please enter a valid phone number";
  }
  if (!message || message.length > 1000) errors.message = "Please enter a message";

  return errors;
}

export function ContactForm() {
  const [values, setValues] = useState({ name: "", phone: "", message: "" });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate(values);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setSent(false);
      return;
    }

    setErrors({});
    const subject = encodeURIComponent(`Blondie's inquiry from ${values.name.trim()}`);
    const body = encodeURIComponent(
      `${values.message.trim()}\n\n— ${values.name.trim()} (${values.phone.trim()})`,
    );
    window.location.href = `mailto:hello@europink.com?subject=${subject}&body=${body}`;
    setSent(true);
    setValues({ name: "", phone: "", message: "" });
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto mt-12 max-w-xl space-y-5 text-left">
      <div>
        <label htmlFor="name" className="mb-1.5 block text-sm font-medium">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={values.name}
          onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
          className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
          placeholder="Your name"
        />
        {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="phone" className="mb-1.5 block text-sm font-medium">
          Phone
        </label>
        <input
          id="phone"
          type="tel"
          value={values.phone}
          onChange={(e) => setValues((v) => ({ ...v, phone: e.target.value }))}
          className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
          placeholder="(415) 555-0123"
        />
        {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
      </div>
      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium">
          Message
        </label>
        <textarea
          id="message"
          rows={4}
          value={values.message}
          onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
          className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
          placeholder="Tell us about your occasion…"
        />
        {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
      </div>
      <button
        type="submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:scale-[1.02]"
      >
        <Send className="h-5 w-5" /> Open Email to Send
      </button>
      <p className="text-center text-sm text-muted-foreground">
        This opens your email app with the message drafted to hello@europink.com — you'll still
        need to press send there. Prefer to talk now? Call 415.335.0586.
      </p>
      {sent && (
        <p className="text-center text-sm text-foreground" role="status">
          Your email app is opening with this message drafted — press send to finish.
        </p>
      )}
    </form>
  );
}
